import React, { FC } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import { BaseCandidate, Document } from 'talent_matching/types';
import DocumentCard from 'shared/components/application_history/document_card';
import DocumentPreview from 'talent_matching/components/shared/document_preview';
import { useUserContext } from 'talent_matching/contexts/user_context';

const t = withLocalePrefix('talent_matching.panel.resume');

type ResumeTabPanelProps = {
  candidate: (BaseCandidate & { attachments?: Document[] }) | null;
  activeDocument: Document | null;
  onOpenDocument: (document: Document) => void;
  canDownloadCandidateData: boolean;
};

const isDocumentSelected = (
  doc: Document,
  activeDocument: Document | null,
  resumeDocument: Document | undefined
): boolean => {
  if (!activeDocument) {
    return false;
  }
  // Direct ID match (user clicked an attachment card)
  if (doc.id === activeDocument.id) {
    return true;
  }
  // Auto-opened resume: activeDocument is resume.document, card is the resume attachment
  if (
    resumeDocument &&
    activeDocument.id === resumeDocument.id &&
    doc.type === 'resume'
  ) {
    return true;
  }
  return false;
};

const ResumeTabPanel: FC<ResumeTabPanelProps> = ({
  candidate,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData,
}) => {
  const { calibrationTerms } = useUserContext();
  const resume = candidate?.resume?.document;
  const attachments = candidate?.attachments ?? [];
  const documents =
    attachments.length > 0 ? attachments : resume ? [resume] : [];

  return (
    <Stack>
      {documents.length > 0 && (
        <Stack direction="column" spacing={2} sx={{ maxHeight: '80vh' }}>
          <Stack spacing={1}>
            <Body medium>{t('documents_heading')}</Body>
            {documents.map((doc, index) => (
              <DocumentCard
                key={doc.id ?? index}
                document={doc}
                selected={isDocumentSelected(doc, activeDocument, resume)}
                onClick={
                  doc.previewable !== false
                    ? () => onOpenDocument(doc)
                    : undefined
                }
                canDownloadCandidateData={canDownloadCandidateData}
              />
            ))}
          </Stack>
          {resume && (
            <Stack spacing={1} sx={{ height: '575px', width: '440px' }}>
              <Body medium>{t('resume_preview_heading')}</Body>
              <DocumentPreview
                document={resume}
                hasCalibrationTerms={calibrationTerms.length > 0}
                onClick={() => {
                  onOpenDocument(resume);
                }}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ResumeTabPanel;
